// @intent Synthesized WebAudio SFX stopgap for match-3 interactions — no audio assets required.
// @flow Lazy AudioContext created on first user pointer event; oscillators synthesize blip/buzz/pop/chime/sting.
// @stale FLAG: real SFX pack is the proper solution (study: ≥7 real SFX in every reference); synthesized is a
//         stopgap — replace when an audio asset pipeline exists (tracked for Wave B).

let audioCtx: AudioContext | null = null;
let masterGain: GainNode | null = null;

const MASTER_GAIN = 0.15;

// Guard for jsdom/headless environments.
function hasAudioContext(): boolean {
  return typeof AudioContext !== 'undefined';
}

// @intent Lazy-init AudioContext on first user gesture (autoplay policy compliance).
// @flow Called by initSfxOnGesture on the first pointer event; subsequent calls are no-ops.
export function initSfxOnGesture(): void {
  if (!hasAudioContext()) return;
  if (audioCtx !== null) return;
  try {
    audioCtx = new AudioContext();
    masterGain = audioCtx.createGain();
    masterGain.gain.value = MASTER_GAIN;
    masterGain.connect(audioCtx.destination);
  } catch {
    audioCtx = null;
    masterGain = null;
  }
}

function getCtx(): { ctx: AudioContext; gain: GainNode } | null {
  if (audioCtx === null || masterGain === null) return null;
  return { ctx: audioCtx, gain: masterGain };
}

// @intent Short blip on accepted swap — brief sine tone.
// @flow Sine osc at 880 Hz, 80ms decay via exponential ramp.
// @stale 2026-06-11 — A6: synthesized SFX.
export function swapBlip(): void {
  const c = getCtx();
  if (!c) return;
  try {
    const { ctx, gain } = c;
    const osc = ctx.createOscillator();
    const env = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = 880;
    env.gain.setValueAtTime(0.6, ctx.currentTime);
    env.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.08);
    osc.connect(env);
    env.connect(gain);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.08);
  } catch { /* fail-silent */ }
}

// @intent Short buzz on denied swap — low sawtooth tone.
// @flow Sawtooth osc at 120 Hz, 120ms decay.
// @stale 2026-06-11 — A6: synthesized SFX.
export function denyBuzz(): void {
  const c = getCtx();
  if (!c) return;
  try {
    const { ctx, gain } = c;
    const osc = ctx.createOscillator();
    const env = ctx.createGain();
    osc.type = 'sawtooth';
    osc.frequency.value = 120;
    env.gain.setValueAtTime(0.5, ctx.currentTime);
    env.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.12);
    osc.connect(env);
    env.connect(gain);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.12);
  } catch { /* fail-silent */ }
}

// @intent Pop on clear; pitch rises with cascade depth.
// @flow Triangle osc at baseFreq * 1.18^(depth-1), 150ms decay.
// @stale 2026-06-11 — A6: synthesized SFX.
export function clearPop(depth: number): void {
  const c = getCtx();
  if (!c) return;
  try {
    const { ctx, gain } = c;
    const baseFreq = 440;
    const freq = baseFreq * Math.pow(1.18, Math.max(0, depth - 1));
    const osc = ctx.createOscillator();
    const env = ctx.createGain();
    osc.type = 'triangle';
    osc.frequency.value = freq;
    env.gain.setValueAtTime(0.7, ctx.currentTime);
    env.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15);
    osc.connect(env);
    env.connect(gain);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.15);
  } catch { /* fail-silent */ }
}

// @intent Ascending chime arpeggio on collect-flight landing.
// @flow Three sine tones (C6, E6, G6) staggered 60ms apart, each 250ms decay.
// @stale 2026-06-11 — A6: synthesized SFX.
export function collectChime(): void {
  const c = getCtx();
  if (!c) return;
  try {
    const { ctx, gain } = c;
    const freqs = [1047, 1319, 1568];
    freqs.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const env = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      const startAt = ctx.currentTime + i * 0.06;
      env.gain.setValueAtTime(0, startAt);
      env.gain.linearRampToValueAtTime(0.5, startAt + 0.02);
      env.gain.exponentialRampToValueAtTime(0.001, startAt + 0.25);
      osc.connect(env);
      env.connect(gain);
      osc.start(startAt);
      osc.stop(startAt + 0.25);
    });
  } catch { /* fail-silent */ }
}

// @intent Four-note ascending sting on win.
// @flow Sine tones C5, E5, G5, C6 staggered 100ms apart, decaying over 600ms total.
// @stale 2026-06-11 — A6: synthesized SFX.
export function winSting(): void {
  const c = getCtx();
  if (!c) return;
  try {
    const { ctx, gain } = c;
    const notes = [523, 659, 784, 1047];
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const env = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      const startAt = ctx.currentTime + i * 0.1;
      env.gain.setValueAtTime(0, startAt);
      env.gain.linearRampToValueAtTime(0.6, startAt + 0.03);
      env.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.55);
      osc.connect(env);
      env.connect(gain);
      osc.start(startAt);
      osc.stop(ctx.currentTime + 0.6);
    });
  } catch { /* fail-silent */ }
}
